import React, { useState, useEffect } from 'react';
import { Crown, Search, Check, RefreshCw, Send, ArrowRight, ArrowLeft, PlayCircle, BookMarked, WifiOff, AlertCircle } from 'lucide-react';

export default function Game({ roomId, username, userId, socket, onGoBack }) {
  const [players, setPlayers] = useState([]);
  const [word, setWord] = useState('');
  const [candidate, setCandidate] = useState(null);
  const [isAdminDef, setIsAdminDef] = useState('');
  const [stage, setStage] = useState('waiting-room'); 
  const [defs, setDefs] = useState([]);
  const [myInput, setMyInput] = useState('');
  
  const [selectedVote, setSelectedVote] = useState(null); 
  const [hasVoted, setHasVoted] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isLoadingWord, setIsLoadingWord] = useState(false);
  const [submittedPlayers, setSubmittedPlayers] = useState([]); 
  const [roundVotes, setRoundVotes] = useState({}); 
  const [wordHistory, setWordHistory] = useState([]); 
  const [previousLeaderId, setPreviousLeaderId] = useState(null);

  // TEMPORIZADOR PARA RENOVAR LOS ANUNCIOS CADA 35 SEGUNDOS
  const [adRefreshKey, setAdRefreshKey] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setAdRefreshKey(Date.now());
    }, 35000);
    return () => clearInterval(interval);
  }, []);

  // SCROLL AUTOMÁTICO AL CAMBIAR DE PANTALLA/STAGE
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stage]);

  useEffect(() => {
    socket.emit('join-room', { roomId, username, userId });

    socket.on('sync-state', (roomState) => {
      setStage(roomState.status);
      if (roomState.word) setWord(roomState.word.word || roomState.word);
      if (roomState.candidateWord) setCandidate(roomState.candidateWord);
      setDefs(roomState.definitions || []);
      setSubmittedPlayers(roomState.submittedPlayers || []);
      setRoundVotes(roomState.roundVotes || {});
      setWordHistory(roomState.wordHistory || []);
      setPreviousLeaderId(roomState.previousLeaderId || null);
      if (roomState.votedPlayers?.includes(socket.id)) setHasVoted(true);
      if (roomState.definitions?.find(d => d.author === socket.id)) setHasSubmitted(true);
      
      // Sincronizar la definición para el moderador si entra a mitad de escritura
      if (roomState.status === 'writing' && roomState.word) {
          setIsAdminDef(roomState.word.definition || '');
      }
    });

    socket.on('update-players', (updatedPlayers) => {
      const unique = updatedPlayers.filter((v,i,a)=>a.findIndex(v2=>(v2.id===v.id))===i);
      setPlayers(unique);
    });
    
    socket.on('game-started-from-waiting', () => setStage('lobby'));
    socket.on('player-submitted', setSubmittedPlayers);

    socket.on('moderator-candidate', (candidateObj) => {
      setCandidate(candidateObj);
      setIsLoadingWord(false);
    });

    socket.on('game-started', (data) => {
      if (data && data.word) setWord(data.word);
      if (data && data.definition) setIsAdminDef(data.definition);
      setStage('writing');
      setCandidate(null);
      setHasSubmitted(false);
      setHasVoted(false);
      setSelectedVote(null);
      setMyInput('');
      setSubmittedPlayers([]);
      setRoundVotes({});
      setIsLoadingWord(false);
    });

    socket.on('admin-definition', setIsAdminDef);
    socket.on('start-voting', (shuffledDefs) => {
      setDefs(shuffledDefs);
      setStage('voting');
    });

    socket.on('show-results', (votesCount) => {
      setRoundVotes(votesCount || {});
      setStage('results');
    });

    socket.on('show-leaderboard', (data) => {
      if (data && data.previousLeaderId) setPreviousLeaderId(data.previousLeaderId);
      setStage('leaderboard');
    });

    socket.on('game-over', (data) => {
      if(data && data.wordHistory) setWordHistory(data.wordHistory);
      if(data && data.previousLeaderId) setPreviousLeaderId(data.previousLeaderId);
      setStage('game-over');
    }); 

    socket.on('new-round-started', () => {
      setStage('lobby');
      setWord('');
      setDefs([]);
      setIsAdminDef(''); // Limpiamos la definición vieja
      setIsLoadingWord(false);
    });

    socket.on('back-to-waiting-room', () => {
      setStage('waiting-room');
      setWord('');
      setDefs([]);
      setIsAdminDef(''); // Limpiamos la definición vieja
      setSubmittedPlayers([]);
      setWordHistory([]);
      setPreviousLeaderId(null);
    });

    return () => {
      socket.off('sync-state');
      socket.off('update-players');
      socket.off('game-started-from-waiting');
      socket.off('player-submitted');
      socket.off('moderator-candidate');
      socket.off('game-started');
      socket.off('admin-definition');
      socket.off('start-voting');
      socket.off('show-results');
      socket.off('show-leaderboard');
      socket.off('game-over');
      socket.off('new-round-started');
      socket.off('back-to-waiting-room');
    };
  }, [roomId, username, userId, socket]);

  const handleProposeWord = () => {
    setIsLoadingWord(true);
    setCandidate(null); 
    socket.emit('propose-word', roomId);
  };

  // Identificación por DNI (userId)
  const me = players.find(p => p.userId === userId) || players.find(p => p.id === socket.id);
  const myId = me?.id || socket.id;
  const isLeader = me?.isLeader || false;
  const leaderName = players.find(p => p.isLeader)?.username || 'El moderador';

  const sortedPlayers = [...players].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points; 
    const timeA = a.correctAnswerTimeTotal > 0 ? a.correctAnswerTimeTotal : Infinity;
    const timeB = b.correctAnswerTimeTotal > 0 ? b.correctAnswerTimeTotal : Infinity;
    return timeA - timeB; 
  });

  const playersWaitingToWrite = players.filter(p => !p.isLeader); 

  const renderWritingStatus = () => (
    <div style={{marginTop: '20px', padding: '15px', background: 'var(--card-white)', borderRadius: 'var(--radius)', border: '1px solid var(--secondary-muted)'}}>
      <h4 style={{margin: '0 0 15px 0', color: 'var(--text-dim)', fontSize: '0.85rem', textTransform: 'uppercase'}}>Estado de los jugadores:</h4>
      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
        {playersWaitingToWrite.map(p => {
          const hasDone = submittedPlayers.includes(p.id);
          const isDisconnected = p.connected === false;
          
          return (
            <div key={p.id} style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', opacity: isDisconnected ? 0.5 : 1}}>
              <span style={{fontWeight: '500', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '6px'}}>
                {p.username} {isDisconnected && <WifiOff size={14} color="var(--text-dim)"/>}
              </span>
              <span style={{color: hasDone ? 'var(--primary-dark)' : 'var(--text-dim)', fontWeight: hasDone ? '700' : 'normal', fontStyle: hasDone ? 'normal' : 'italic'}}>
                {isDisconnected ? 'Desconectado' : (hasDone ? 'Listo' : 'Escribiendo...')}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  );

  const handleSubmitDefinition = () => {
    let text = myInput.trim();
    if (!text) return; 

    text = text.charAt(0).toUpperCase() + text.slice(1);

    if (!text.endsWith('.')) {
      text += '.';
    }

    socket.emit('submit-definition', { roomId, text });
    setHasSubmitted(true);
  };

  const showNativeAd = 
    stage === 'waiting-room' || 
    stage === 'lobby' || 
    (stage === 'writing' && isLeader) || 
    (stage === 'writing' && !isLeader && hasSubmitted);

  return (
    <div className="game-container">
      <button className="btn-back-top" onClick={onGoBack}>
        <ArrowLeft size={20} /> Salir
      </button>

      {/* --- SALA DE ESPERA --- */}
      {stage === 'waiting-room' && (
        <div className="card">
          <h2 style={{color: 'var(--primary-dark)', fontSize: '1.8rem', letterSpacing: '2px', marginBottom: '5px'}}>SALA: {roomId}</h2>
          <p style={{color: 'var(--text-dim)', marginBottom: '25px', fontWeight: '500'}}>JUGADORES CONECTADOS</p>
          
          <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '30px'}}>
            {players.map(p => {
              const isMe = p.userId === userId;
              const isDisconnected = p.connected === false;
              
              return (
                <div key={p.id} style={{
                  padding: '16px', background: isMe ? '#F1F5F9' : 'var(--bg-light)', borderRadius: '8px', 
                  border: isMe ? '2px solid var(--accent-blue)' : '1px solid var(--secondary-muted)', 
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  boxShadow: isMe ? '0 4px 6px rgba(0,0,0,0.02)' : 'none',
                  opacity: isDisconnected ? 0.5 : 1
                }}>
                  <span style={{fontWeight: isMe ? '800' : '600', color: isMe ? 'var(--primary-dark)' : 'var(--text-main)', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px'}}>
                    {p.username} {isDisconnected && <WifiOff size={16} color="var(--text-dim)"/>}
                  </span>
                  {p.isLeader && <span style={{fontSize: '0.85rem', color: 'var(--text-dim)', fontWeight: '600', textTransform: 'uppercase'}}>(Moderador)</span>}
                </div>
              )
            })}
          </div>

          {isLeader ? (
            <button onClick={() => {
                const activePlayers = players.filter(p => p.connected !== false);
                if (activePlayers.length < 2) alert("¡No puedes jugar solo! Espera a que se una al menos un amigo.");
                else socket.emit('start-game', roomId);
              }} style={{background: 'var(--success-teal)', padding: '18px', fontSize: '1.1rem'}}>
              EMPEZAR PARTIDA
            </button>
          ) : (
            <div style={{padding: '15px', background: 'var(--bg-light)', borderRadius: '8px', border: '1px solid var(--secondary-muted)'}}>
              <p style={{fontSize: '0.95rem', color: 'var(--text-dim)', margin: 0}}>Esperando a que <strong>{leaderName}</strong> empiece la partida...</p>
            </div>
          )}
        </div>
      )}

      {/* --- LOBBY DEL MODERADOR --- */}
      {stage !== 'waiting-room' && stage !== 'game-over' && (
        <div className="card">
          <h2>SALA: {roomId}</h2>
          
          {stage === 'lobby' && (
            <div>
              {isLeader ? (
                <div style={{ paddingBottom: '10px' }}>
                  {!candidate ? (
                    <button onClick={handleProposeWord} disabled={isLoadingWord} style={{minHeight: '60px'}}>
                      {!isLoadingWord && <Search size={24} className="icon-btn" />} 
                      <span style={{marginLeft: 10}}>{isLoadingWord ? "BUSCANDO PALABRA..." : "BUSCAR PALABRA"}</span>
                    </button>
                  ) : (
                    <div style={{animation: 'fadeIn 0.5s'}}>
                      <div className="word-highlight" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', fontSize: candidate.word?.length > 12 ? '1.5rem' : '2.2rem' }}>
                        {candidate.word}
                      </div>
                      <div style={{background: 'var(--bg-light)', border: '1px solid var(--secondary-muted)', padding: '20px', borderRadius: 'var(--radius)', marginBottom: '25px'}}>
                           <p style={{fontStyle: 'italic', fontSize: '1rem', margin: 0, lineHeight: 1.6, color: 'var(--primary-dark)'}}>"{candidate.definition}"</p>
                      </div>
                      <div style={{display: 'flex', gap: '10px'}}>
                        <button onClick={() => {
                          setIsAdminDef(candidate.definition); // Guardamos la definición preventivamente
                          socket.emit('select-word', roomId);
                        }} className="btn-join" style={{background: 'var(--success-teal)', color: 'white', borderColor: 'var(--success-teal)'}}>
                          <Check size={20} className="icon-btn" /> ACEPTAR
                        </button>
                        <button onClick={handleProposeWord} className="btn-create" disabled={isLoadingWord} style={{background: 'var(--text-dim)'}}>
                           <RefreshCw size={20} className="icon-btn" /> OTRA
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{padding: '30px 20px'}}>
                  <p style={{fontSize: '1.2rem', color: 'var(--text-dim)', fontWeight: '500'}}>{leaderName} está eligiendo una palabra...</p>
                </div>
              )}
            </div>
          )}

          {stage !== 'lobby' && word && (
            <div className="word-highlight" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', fontSize: word?.length > 12 ? '1.5rem' : '2.2rem' }}>
              {word}
            </div>
          )}

          {/* --- ESCRIBIENDO DEFINICIONES --- */}
          {stage === 'writing' && (
            <div>
               {isLeader ? (
                <div style={{textAlign: 'left', background: 'var(--bg-light)', border: '1px solid var(--secondary-muted)', padding: '25px', borderRadius: 'var(--radius)'}}>
                  <p style={{fontSize: '0.85rem', color: 'var(--accent-blue)', fontWeight: '700', textTransform: 'uppercase', marginBottom: '10px', letterSpacing: '1px'}}>DEFINICIÓN</p>
                  <p style={{lineHeight: '1.6', fontSize: '1.05rem', margin: 0}}>{isAdminDef}</p>
                  {renderWritingStatus()}
                </div>
              ) : (
                <>
                  {!hasSubmitted ? (
                    <>
                      <p style={{fontWeight: '500', marginBottom: '15px'}}>Inventa tu definición:</p>
                      <textarea value={myInput} onChange={e => setMyInput(e.target.value)} rows="4" placeholder="Escribe aquí..." />
                      <button onClick={handleSubmitDefinition}>
                        <Send size={18} className="icon-btn"/> ENVIAR DEFINICIÓN
                      </button>
                    </>
                  ) : (
                    <div style={{padding: '10px', animation: 'fadeIn 0.5s'}}>
                      <h3 style={{color: 'var(--primary-dark)', marginBottom: '20px'}}>¡Definición enviada!</h3>
                      {renderWritingStatus()}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* --- VOTACIÓN EN 2 PASOS --- */}
          {stage === 'voting' && (
            <div>
              <h3 style={{marginBottom: '20px'}}>HORA DE VOTAR</h3>
              <p style={{color: 'var(--text-dim)', marginBottom: '15px', fontSize: '0.9rem'}}>
                 {isLeader ? 'Eres el moderador en esta ronda.' : (hasVoted ? 'Voto enviado, esperando al resto...' : 'Selecciona la que creas que es real y pulsa Enviar.')}
              </p>
              
              <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                {defs.map((d, i) => {
                  const isMyOwn = d.author === myId;
                  const isSelected = selectedVote === d.id;
                  const canSelect = !hasVoted && !isLeader && !isMyOwn;

                  return (
                    <button key={i} onClick={() => { if(canSelect) setSelectedVote(d.id); }} 
                            disabled={!canSelect}
                            style={{
                              background: isSelected ? '#E0E7FF' : ((hasVoted || isMyOwn || isLeader) ? 'var(--bg-light)' : 'var(--card-white)'), 
                              border: isSelected ? '2px solid var(--accent-blue)' : '1px solid var(--secondary-muted)',
                              color: 'var(--text-main)',
                              padding: '20px', textAlign: 'left', fontSize: '1rem', fontWeight: 'normal', lineHeight: '1.5',
                              opacity: (hasVoted && !isLeader) || isMyOwn ? 0.6 : 1, 
                              cursor: canSelect ? 'pointer' : 'default',
                              boxShadow: isSelected ? '0 4px 12px rgba(59, 130, 246, 0.15)' : ((hasVoted || isMyOwn || isLeader) ? 'none' : '0 2px 8px rgba(0,0,0,0.02)'),
                              transform: isSelected ? 'scale(1.02)' : 'none',
                              transition: 'all 0.2s ease'
                            }}>
                      {d.text}
                      {isMyOwn && <span style={{display: 'block', marginTop: '8px', fontSize: '0.8rem', color: 'var(--accent-blue)', fontWeight: '600'}}>Esta es tu definición</span>}
                    </button>
                  )
                })}
                
                {/* BOTÓN DE ENVIAR VOTO */}
                {!isLeader && !hasVoted && selectedVote && (
                  <button 
                    onClick={() => { socket.emit('vote', { roomId, definitionId: selectedVote }); setHasVoted(true); }}
                    style={{background: 'var(--success-teal)', marginTop: '15px', padding: '15px', fontSize: '1.05rem', animation: 'fadeIn 0.3s'}}
                  >
                    <Check size={20} style={{verticalAlign: 'middle', marginRight: '8px'}} /> ENVIAR RESPUESTA
                  </button>
                )}
                {hasVoted && !isLeader && <p style={{color: 'var(--primary-dark)', marginTop: '15px', fontWeight: '600'}}>✅ Tu voto ha sido registrado</p>}
              </div>
            </div>
          )}

          {/* --- PANTALLA 1: RESULTADOS DEL TURNO --- */}
          {stage === 'results' && (
            <div>
              <h3 style={{color: 'var(--accent-blue)', marginBottom: '25px'}}>¡RESULTADOS DE LA RONDA!</h3>
              
              <div style={{display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '30px'}}>
                {defs.map((d, i) => {
                   const votesCount = roundVotes[d.id] || 0;
                   return (
                    <div key={i} style={{
                        padding: '20px', borderRadius: 'var(--radius)',
                        background: d.id === 'rae' ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-light)',
                        border: d.id === 'rae' ? '2px solid var(--success-teal)' : '1px solid var(--secondary-muted)',
                        textAlign: 'left', position: 'relative'
                    }}>
                      <p style={{margin: 0, fontSize: '1.05rem', lineHeight: '1.5', color: 'var(--primary-dark)', fontWeight: d.id === 'rae' ? '600' : 'normal', paddingBottom: votesCount > 0 ? '25px' : '0'}}>{d.text}</p>
                      <p style={{margin: '10px 0 0 0', fontSize: '0.8rem', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: d.id === 'rae' ? 'var(--success-teal)' : 'var(--text-dim)'}}>
                        {d.id === 'rae' ? 'DEFINICIÓN REAL' : `DEFINICIÓN DE ${players.find(p=>p.id===d.author)?.username || 'Alguien'}`}
                      </p>
                      
                      {/* CHIVATO DE VOTOS ABAJO A LA DERECHA */}
                      {votesCount > 0 && (
                        <div style={{position: 'absolute', bottom: '15px', right: '15px', background: 'var(--primary-dark)', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 'bold'}}>
                          {votesCount} {votesCount === 1 ? 'voto' : 'votos'}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {isLeader ? (
                 <button onClick={() => socket.emit('go-to-leaderboard', roomId)} style={{background: 'var(--primary-dark)'}}>
                   VER CLASIFICACIÓN <ArrowRight size={20} style={{marginLeft: 10, verticalAlign: 'middle'}}/>
                 </button>
              ) : (
                 <p style={{fontSize: '0.95rem', color: 'var(--text-dim)', fontWeight: '500'}}>Esperando a que {leaderName} avance a la clasificación...</p>
              )}
            </div>
          )}

          {/* --- PANTALLA 2: CLASIFICACIÓN TRAS LOS RESULTADOS --- */}
          {stage === 'leaderboard' && (
            <div>
              <h3 style={{color: 'var(--primary-dark)', marginBottom: '25px'}}>CLASIFICACIÓN ACTUAL</h3>
              <div className="player-list" style={{marginBottom: '30px'}}>
                {sortedPlayers.map(p => {
                  const isMe = p.userId === userId;
                  const wasModerator = p.id === previousLeaderId;
                  const isDisconnected = p.connected === false;
                  
                  return (
                    <div key={p.id} className="player-item" style={{
                      background: isMe ? '#F1F5F9' : 'var(--card-white)', border: isMe ? '2px solid var(--accent-blue)' : '1px solid var(--secondary-muted)',
                      opacity: isDisconnected ? 0.5 : 1
                    }}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                        <span style={{fontSize: '1.05rem', fontWeight: isMe ? '800' : '600', color: isMe ? 'var(--primary-dark)' : 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '6px'}}>
                          {p.username} {isDisconnected && <WifiOff size={14} color="var(--text-dim)"/>}
                          <span style={{fontSize: '0.8rem', color: 'var(--text-dim)', fontWeight: 'normal'}}>{wasModerator ? '(Moderador)' : ''}</span>
                        </span>
                      </div>
                      <div className="score-badge" style={{fontSize: '1rem', padding: '6px 16px', background: 'var(--primary-dark)'}}>{p.points} pts</div>
                    </div>
                  )
                })}
              </div>

              {isLeader ? (
                 <button onClick={() => socket.emit('start-next-round', roomId)} style={{background: 'var(--success-teal)'}}>
                   INICIAR SIGUIENTE RONDA
                 </button>
              ) : (
                 <p style={{fontSize: '0.95rem', color: 'var(--text-dim)', fontWeight: '500'}}>Esperando a que {leaderName} (nuevo moderador) inicie la ronda...</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* --- PANTALLA FINAL E HISTORIAL --- */}
      {stage === 'game-over' && (
        <div className="card" style={{border: '2px solid var(--accent-blue)', maxWidth: '600px'}}>
          <Crown size={50} color="#F59E0B" style={{margin: '0 auto 15px auto', display: 'block'}} />
          <h2 style={{color: 'var(--primary-dark)', fontSize: '2rem'}}>¡FIN DEL JUEGO!</h2>
          <p style={{color: 'var(--text-dim)', marginBottom: '30px'}}>Todos habéis sido moderadores.</p>
          
          <div className="player-list" style={{marginBottom: '40px'}}>
            {sortedPlayers.map((p, idx) => {
              const isMe = p.userId === userId;
              const isDisconnected = p.connected === false;

              return (
                <div key={p.id} className="player-item" style={{
                  background: isMe ? '#E0E7FF' : 'var(--card-white)', border: isMe ? '2px solid var(--accent-blue)' : '1px solid var(--secondary-muted)',
                  transform: idx === 0 ? 'scale(1.05)' : 'none', boxShadow: idx === 0 ? '0 10px 15px -3px rgba(0,0,0,0.1)' : 'none',
                  opacity: isDisconnected ? 0.5 : 1
                }}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <span style={{fontWeight: '700', fontSize: '1.2rem', color: 'var(--text-dim)'}}>#{idx + 1}</span>
                    <span style={{fontSize: '1.1rem', fontWeight: isMe ? '800' : '600', color: isMe ? 'var(--primary-dark)' : 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '6px'}}>
                      {p.username} {isDisconnected && <WifiOff size={16} color="var(--text-dim)"/>}
                    </span>
                  </div>
                  <div className="score-badge" style={{background: 'var(--primary-dark)', fontSize: '1.1rem', padding: '8px 18px'}}>{p.points}</div>
                </div>
              )
            })}
          </div>

          <div style={{textAlign: 'left', marginBottom: '30px', borderTop: '1px solid var(--secondary-muted)', paddingTop: '20px'}}>
            <h3 style={{color: 'var(--text-dim)', fontSize: '1rem', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px'}}>
              <BookMarked size={18}/> Palabras jugadas
            </h3>
            <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
              {wordHistory.map((item, idx) => (
                <div key={idx} style={{background: 'var(--bg-light)', padding: '15px', borderRadius: '8px', border: '1px solid var(--secondary-muted)'}}>
                  <strong style={{color: 'var(--primary-dark)', fontSize: '1.1rem', display: 'block', marginBottom: '5px'}}>{item.word}</strong>
                  <span style={{fontSize: '0.9rem', color: 'var(--text-main)', lineHeight: '1.4'}}>{item.definition}</span>
                </div>
              ))}
            </div>
          </div>

          {isLeader ? (
            <button onClick={() => socket.emit('play-again', roomId)} style={{background: 'var(--success-teal)'}}>
              <RefreshCw size={20} style={{verticalAlign: 'middle', marginRight: '8px'}}/> VOLVER A JUGAR
            </button>
          ) : (
            <p style={{fontSize: '0.95rem', color: 'var(--text-dim)'}}>Esperando a que {leaderName} reinicie la sala...</p>
          )}
        </div>
      )}

      {/* --- BANNER DE PUBLICIDAD NATIVO (Fijo en el DOM para evitar re-cargas, adaptable al móvil) --- */}
      {showNativeAd && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '25px', animation: 'fadeIn 0.5s', width: '100%' }}>
          <iframe 
            title="Adsterra Native"
            src={`${process.env.PUBLIC_URL}/ad-banner-small.html?v=${adRefreshKey}`} 
            width="100%" 
            height="300" 
            frameBorder="0" 
            scrolling="no" 
            allowTransparency="true"
            sandbox="allow-scripts allow-popups allow-same-origin"
            style={{ maxWidth: '350px', border: 'none', overflow: 'hidden' }}
          ></iframe>
        </div>
      )}

      {/* --- BANNER DE PUBLICIDAD PEQUEÑO FIJO (Aparece siempre abajo, renovándose) --- */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        width: '100%', 
        marginTop: 'auto', 
        paddingTop: '20px',
        paddingBottom: '10px'
      }}>
        <iframe 
          title="Adsterra Banner Small"
          src={`${process.env.PUBLIC_URL}/ad-banner-small.html?v=${adRefreshKey}`} 
          width="320" 
          height="50" 
          frameBorder="0" 
          scrolling="no" 
          allowTransparency="true" 
          sandbox="allow-scripts allow-popups allow-same-origin"
          style={{ border: 'none', overflow: 'hidden' }}
        ></iframe>
      </div>
    </div>
  );
}